<template>
  <div class="text-left">
    <RejectCommentLayout :comment-text="reject_comment" />
    <v-form ref="form">
      <v-stepper v-model="step">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">{{
            $t("form.basicData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="2" :complete="step > 2">{{
            $t("form.administrativeDate")
          }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <h5>
              {{ $t("form.givenAndFamilyName") }}
            </h5>
            <v-row>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.preferred_name.given_name"
                  @blur="normalizePersonNameField(investigator.person.preferred_name, 'given_name')"
                  :label="$t('form.name')"
                  required
                  outlined
                  :readonly="disabledForm"
                  :rules="rules.nameRules"
                  :counter="40"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.preferred_name.family_name"
                  @blur="normalizePersonNameField(investigator.person.preferred_name, 'family_name')"
                  :label="$t('form.familyName')"
                  required
                  outlined
                  :readonly="disabledForm"
                  :rules="rules.nameRules"
                  :counter="40"
                ></v-text-field>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.givenAndFamilyNameAlt") }}
            </h5>
            <v-row>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.alt_name.given_name"
                  @blur="normalizePersonNameField(investigator.person.alt_name, 'given_name')"
                  :label="$t('form.name')"
                  required
                  outlined
                  :readonly="disabledForm"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.alt_name.family_name"
                  @blur="normalizePersonNameField(investigator.person.alt_name, 'family_name')"
                  :label="$t('form.familyName')"
                  required
                  outlined
                  :readonly="disabledForm"
                ></v-text-field>
              </v-col>
            </v-row>
            <h5>{{ $t("form.email") }}</h5>
            <ArrayFieldForm
              v-model="investigator.person.email"
              :read-only="disabledForm"
              name-field="email"
              :label="$t('form.email')"
            />
            <h5>{{ $t("form.incharge") }}</h5>
            <SelectPeopleList
              :disabled-form="disabledForm"
              :items-selected="personsInCharge"
            />
            <h5>
              {{ $t("form.publication.research_areas") }}
            </h5>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-select
                  :items="researchAreas"
                  v-model="investigator.person.research_areas"
                  :label="$t('form.publication.research_areas')"
                  required
                  :item-text="'name.' + $i18n.locale"
                  item-value="id"
                  outlined
                  multiple
                  :readonly="disabledForm"
                  :rules="rules.fieldRequired"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-row>
              <v-col cols="12" md="6" class="py-0">
                <h5>
                  {{ $t("form.dni") }}
                </h5>
                <v-row>
                  <v-col cols="4" class="py-0">
                    <v-select
                      v-model="investigator.person.id_number.type"
                      :label="$t('type')"
                      outlined
                      :items="identificationOptions"
                      item-text="text"
                      item-value="value"
                      :readonly="disabledForm"
                      :rules="rules.fieldRequired"
                      @change="$refs.idNumberText.validate()"
                    ></v-select
                  ></v-col>
                  <v-col cols="8" class="py-0">
                    <v-text-field
                      v-model="investigator.person.id_number.value"
                      :label="$t('label.value')"
                      ref="idNumberText"
                      required
                      outlined
                      :readonly="disabledForm"
                      :rules="[dniValueRulesFunction]"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <h5>
                  {{ $t("form.mobile") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="investigator.person.number"
                      outlined
                      :label="$t('form.mobile')"
                      :readonly="disabledForm"
                      :rules="[telephoneRuleFunction]"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <h5>{{ $t("form.gender") }}</h5>
                <v-row>
                  <v-col class="py-0 mt-1">
                    <v-btn-toggle
                      v-model="investigator.person.gender"
                      mandatory
                      :disabled="disabledForm"
                    >
                      <v-btn value="male" :class="{'disable-events': disabledForm}">
                        {{ $t("form.male") }}
                      </v-btn>
                      <v-btn value="female" :class="{'disable-events': disabledForm}">
                        {{ $t("form.female") }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-divider />
      <UserFormActions
        form_name_text="External people"
        :form_name="form_name"
        :refForm="$refs.form"
        :object_type="object_type"
        :data="processData"
        :state="state"
        @change-state="changeState"
        :paginated-form="true"
        class="text-center mt-6"
      />
    </v-form>
  </div>
</template>

<script>
import axios from "axios";
import RejectCommentLayout from "../../layouts/RejectCommentLayout";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import SelectPeopleList from "../../reusables/SelectPeopleList";
import UserFormActions from "../../action_buttons/UserFormActions";
import changeState from "../../../mixins/changeState";
import validationRules from "../../../mixins/validationRules";
import investigatorData from "../../../mixins/investigator_data";
import investigator_model from "../../../mixins/model_utils/investigator_model";

export default {
  mixins: [changeState, validationRules, investigatorData, investigator_model],
  components: {
    RejectCommentLayout,
    ArrayFieldForm,
    SelectPeopleList,
    UserFormActions
  },

  data: () => ({
    form_name: "personalForm",
    maxBirthdayDate: "",
    step: 1,
    menu: false,
    reject_comment: "",
    object_type: "investigator"
  }),

  created() {
    this.loadBirthdayDate();
    this.getResearchAreas();
    if (this.updatePersonal || this.updatePersonalForm) this.getData();
  },

  computed: {
    createPersonal() {
      return !(this.$route.params.id > -1) && !this.updatePersonal;
    },
    updatePersonalForm() {
      return this.$route.params.id > -1 && !this.updatePersonal;
    },
    updatePersonal() {
      return this.$route.params.id > -1 && this.$route.meta.update;
    },
    disabledForm() {
      let states = ["VD", "PA", "PV", "PC"];
      return states.includes(this.state);
    },

    processData() {
      let number = "";
      if (typeof this.investigator.person.number === "number") {
        number = this.investigator.person.number.toString();
      } else if (typeof this.investigator.person.number === "string") {
        number = this.investigator.person.number.replace(/[^\d]/g, "");
      }
      let emails = [];
      this.investigator.person.email.forEach(email => {
        emails.push(email.trim());
      });
      let data = {
        title: this.investigator.name,
        object_type: this.object_type,
        form_name: "personalForm",
        object_id: this.investigator.id,
        data: {
          user: {
            person: {
              preferred_name: {
                given_name:
                  this.investigator.person.preferred_name.given_name !==
                  undefined
                    ? this.investigator.person.preferred_name.given_name.trim()
                    : "",
                family_name:
                  this.investigator.person.preferred_name.family_name !==
                  undefined
                    ? this.investigator.person.preferred_name.family_name.trim()
                    : ""
              },
              alt_name: {
                given_name:
                  this.investigator.person.alt_name.given_name !== undefined
                    ? this.investigator.person.alt_name.given_name.trim()
                    : "",
                family_name:
                  this.investigator.person.alt_name.family_name !== undefined
                    ? this.investigator.person.alt_name.family_name.trim()
                    : ""
              },
              email: emails,
              incharge: this.personsInCharge,
              id_number: this.investigator.person.id_number,
              gender: this.investigator.person.gender,
              number: number,
              research_areas: this.investigator.person.research_areas.map(
                area => (typeof area === "number" ? area : area.id)
              )
            }
          }
        }
      };
      return data;
    }
  },

  methods: {
    loadBirthdayDate() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 15);
      this.maxBirthdayDate = date.toISOString().substr(0, 10);
    },
    getData() {
      if (this.updatePersonal) {
        this.getInvestigatorAndPersonsInCharge(
          this.$store.state.endpoint + "/person/" + this.$route.params.id
        );
      } else {
        let path =
          this.$store.state.endpoint +
          "/user/moderatedOperations/investigator/" +
          this.$route.params.id;
        axios
          .get(path)
          .then(response => {
            this.investigator.id = response.data.object_id;
            this.investigator.person = response.data.data.user.person;
            this.personsInCharge = response.data.data.user.person.incharge;
            this.state = response.data.state;
            this.reject_comment = response.data.reject_comment;
            this.getInvestigatorName(
              this.$store.state.endpoint + "/person/" + this.investigator.id
            );
          })
          .catch(error => {
            console.log(error);
          });
      }
    }
  }
};
</script>
